import React, { useState } from "react";
import { useMutation } from "@apollo/client/react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";

import InternalWorkOrderForm from "./InternalWorkOrderForm";
import InternalWorkOrderSuccess from "./InternalWorkOrderSuccess";

export default function InternalWorkOrderModal({
  inventoryRequestId,
  projectId,
  warehouse,
  warehouses,
  allWarehouses,
  costCenters,
  internalWorkOrderTypes,
  internalBillingTypes,
  isCartContext,
  isProjectContext,
  onClose,
  query,
  onSuccess,
  displaySuccessAction,
}) {
  const [createInternalWorkOrder, { loading: submitting }] = useMutation(query);
  const [createdInternalWorkOrder, setCreatedInternalWorkOrder] = useState(null);

  const handleSubmit = async values => {
    const workOrderTypeValue =
      typeof values.internalWorkOrderType === "string"
        ? values.internalWorkOrderType
        : values.internalWorkOrderType?.value;

    // Transform flat rate amount into billing_quantities format if applicable
    let billingQuantities = undefined;
    if (values.billingType === 'Flat Rate/As Quoted (Internal Work Orders)' && values.flatRateAmount) {
      billingQuantities = [{
        quantity_type: 'flat_rate',
        quantity_count: parseFloat(values.flatRateAmount)
      }];
    }

    const { data } = await createInternalWorkOrder({
      variables: {
        input: {
          warehouseId: values.warehouse?.warehouseId,
          destinationWarehouseId: values.destinationWarehouse?.warehouseId,
          costCenterId: values.costCenter?.id || undefined,
          internalWorkOrderType: workOrderTypeValue || undefined,
          projectId: values.projectId,
          deadlineDate: values.deadlineDate
            ? moment(values.deadlineDate).format("YYYY-MM-DD")
            : undefined,
          comment: values.comment,
          billingType: values.billingType || undefined,
          billingQuantities: billingQuantities,
          vaStatus: {
            submitted: true,
          },
        },
        inventoryRequestId,
      },
    });
    const response = data?.createInternalWorkOrder || data?.makeInternalWorkOrderFromCart || {};
    const errors = response?.errors || [];
    if (errors.length === 0) {
      setCreatedInternalWorkOrder(response.internalWorkOrder);
      if (onSuccess) onSuccess(response.internalWorkOrder);
    }
  };

  const handleClose = () => {
    setCreatedInternalWorkOrder(null);
    onClose();
  };

  return (
    <Dialog open onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ position: "relative" }}>
        <Typography align="center" fontSize={22} fontWeight={500} color="text.primary">
          Create Internal Work Order
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      {createdInternalWorkOrder ? (
        <InternalWorkOrderSuccess
          internalWorkOrder={createdInternalWorkOrder}
          projectId={projectId}
          displaySuccessAction={displaySuccessAction}
          onClose={handleClose}
        />
      ) : (
        <InternalWorkOrderForm
          projectId={projectId}
          warehouse={warehouse}
          warehouses={warehouses}
          allWarehouses={allWarehouses || warehouses}
          costCenters={costCenters || []}
          internalWorkOrderTypes={internalWorkOrderTypes || []}
          internalBillingTypes={internalBillingTypes || []}
          isCartContext={isCartContext}
          isProjectContext={isProjectContext}
          onSubmit={handleSubmit}
          onClose={handleClose}
          isSubmitting={submitting}
        />
      )}
    </Dialog>
  );
}
